import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ImplementationSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [activePhase, setActivePhase] = useState(0);

  const translations = {
    en: {
      title: "Seamless Implementation Process",
      subtitle: "Our proven 6-phase implementation methodology ensures smooth deployment with minimal business disruption",
      phases: [
        {
          phase: 1,
          title: "Assessment & Planning",
          icon: "ClipboardList",
          duration: "Week 1-2",
          description: "Comprehensive analysis of your current processes and customization requirements",
        },
        {
          phase: 2,
          title: "System Setup & Configuration",
          icon: "Settings",
          duration: "Week 3-4",
          description: "ERP system installation, configuration, and customization based on your requirements",
        },
        {
          phase: 3,
          title: "Data Migration",
          icon: "Database",
          duration: "Week 5-6",
          description: "Secure transfer of your existing data to the new ERP system with validation",
        },
        {
          phase: 4,
          title: "Testing & Quality Assurance",
          icon: "CheckCircle",
          duration: "Week 7-8",
          description: "Comprehensive testing to ensure system functionality and performance",
        },
        {
          phase: 5,
          title: "Training & Knowledge Transfer",
          icon: "Users",
          duration: "Week 9-10",
          description: "Comprehensive training for your team to ensure effective system utilization",
        },
        {
          phase: 6,
          title: "Go-Live & Support",
          icon: "Rocket",
          duration: "Week 11-12",
          description: "System deployment with continuous monitoring and immediate support",
        }
      ],
      timeline: {
        total: "12 weeks average implementation time",
        support: "3 months post-implementation support included"
      }
    },
    hi: {
      title: "निर्बाध कार्यान्वयन प्रक्रिया",
      subtitle: "हमारी सिद्ध 6-चरण कार्यान्वयन पद्धति न्यूनतम व्यावसायिक व्यवधान के साथ सुचारू तैनाती सुनिश्चित करती है",
      phases: [
        {
          phase: 1,
          title: "मूल्यांकन और योजना",
          icon: "ClipboardList",
          duration: "सप्ताह 1-2",
          description: "आपकी वर्तमान प्रक्रियाओं और अनुकूलन आवश्यकताओं का व्यापक विश्लेषण",
        },
        {
          phase: 2,
          title: "सिस्टम सेटअप और कॉन्फ़िगरेशन",
          icon: "Settings",
          duration: "सप्ताह 3-4",
          description: "आपकी आवश्यकताओं के आधार पर ERP सिस्टम इंस्टॉलेशन, कॉन्फ़िगरेशन और अनुकूलन",
        },
        {
          phase: 3,
          title: "डेटा माइग्रेशन",
          icon: "Database",
          duration: "सप्ताह 5-6",
          description: "सत्यापन के साथ आपके मौजूदा डेटा का नए ERP सिस्टम में सुरक्षित स्थानांतरण",
        },
        {
          phase: 4,
          title: "परीक्षण और गुणवत्ता आश्वासन",
          icon: "CheckCircle",
          duration: "सप्ताह 7-8",
          description: "सिस्टम कार्यक्षमता और प्रदर्शन सुनिश्चित करने के लिए व्यापक परीक्षण",
        },
        {
          phase: 5,
          title: "प्रशिक्षण और ज्ञान स्थानांतरण",
          icon: "Users",
          duration: "सप्ताह 9-10",
          description: "प्रभावी सिस्टम उपयोग सुनिश्चित करने के लिए आपकी टीम के लिए व्यापक प्रशिक्षण",
        },
        {
          phase: 6,
          title: "गो-लाइव और सहायता",
          icon: "Rocket",
          duration: "सप्ताह 11-12",
          description: "निरंतर निगरानी और तत्काल सहायता के साथ सिस्टम तैनाती",
        }
      ],
      timeline: {
        total: "12 सप्ताह औसत कार्यान्वयन समय",
        support: "3 महीने कार्यान्वयन के बाद सहायता शामिल"
      }
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  return (
    <section id="implementation" className="py-24 bg-gradient-to-br from-primary-50 via-background to-surface-100 relative overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4 drop-shadow-sm">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
        {/* Stepper Timeline */}
        <div className="relative flex flex-col md:flex-row items-center md:items-start justify-between gap-8 md:gap-0">
          {/* Connector Line */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-2 -z-10">
            <div className="h-full w-full bg-gradient-to-r from-accent-200 via-accent-400 to-accent-600 rounded-full opacity-30" />
          </div>
          {t.phases.map((phase, idx) => (
            <div
              key={phase.phase}
              className={`group flex-1 flex flex-col items-center md:items-start md:max-w-xs transition-all duration-300`}
              onMouseEnter={() => setActivePhase(idx)}
              onFocus={() => setActivePhase(idx)}
              tabIndex={0}
            >
              {/* Icon Circle */}
              <div className={`relative z-10 flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/60 backdrop-blur-md shadow-lg border-4 border-accent-200 group-hover:scale-105 transition-transform duration-300 ${activePhase === idx ? 'ring-4 ring-accent-400' : ''}`}>
                <Icon name={phase.icon} size={40} color="var(--color-accent)" />
                {/* Animated Glow */}
                {activePhase === idx && (
                  <span className="absolute inset-0 rounded-full bg-accent-400 opacity-20 animate-pulse" />
                )}
              </div>
              {/* Card */}
              <div className={`mt-6 p-6 rounded-2xl bg-white/70 backdrop-blur-xl shadow-cta border border-border transition-all duration-300 ${activePhase === idx ? 'scale-105 border-accent-400 shadow-lg' : ''}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-accent font-bold text-lg">Phase {phase.phase}</span>
                  <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full ml-2">{phase.duration}</span>
                </div>
                <h3 className="text-xl font-semibold text-primary mb-2">{phase.title}</h3>
                <p className="text-base text-text-secondary mb-2">{phase.description}</p>
              </div>
            </div>
          ))}
        </div>
        {/* Timeline Info */}
        <div className="mt-12 flex flex-col md:flex-row items-center justify-center gap-6">
          <div className="flex items-center gap-3 bg-white/60 backdrop-blur-md px-6 py-3 rounded-full shadow-card border border-border">
            <Icon name="Clock" size={20} color="var(--color-primary)" />
            <span className="text-primary font-medium">{t.timeline.total}</span>
                </div>
          <div className="flex items-center gap-3 bg-white/60 backdrop-blur-md px-6 py-3 rounded-full shadow-card border border-border">
            <Icon name="LifeBuoy" size={20} color="var(--color-success)" />
            <span className="text-success font-medium">{t.timeline.support}</span>
          </div>
        </div>
      </div>
      {/* Decorative Gradient Blobs */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-gradient-to-br from-accent-200 via-accent-400 to-accent-600 rounded-full opacity-20 blur-3xl -z-20 animate-pulse" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-gradient-to-tr from-primary-200 via-primary-400 to-primary-600 rounded-full opacity-20 blur-3xl -z-20 animate-pulse" />
    </section>
  );
};

export default ImplementationSection;